import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import TableDataComponent from "components/organisms/TableData";
import React, { useCallback, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import IRSResult from "services/IRSResult";
import { errorToastNotif, setDataToLocalStorage } from "utils/common";
import { MonthOptions, PageMode } from "utils/constant";

const IRSResultService = new IRSResult();

const TableComponent = ({ changeMode }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const { currentUser } = useSelector((state) => state.authReducer);

  const fetchVectorControlResults = useCallback(async () => {
    setIsLoading(true);
    try {
      const { page, limit } = dataState;

      const searchBy = `fasyankes_id${
        searchTerm !== "" ? `,urbanvillage_id` : ""
      }`;
      const searchValue = `${currentUser.fasyankes_id}${
        searchTerm !== "" ? `,${searchTerm}` : ""
      }`;

      await IRSResultService.getAll({
        page: page,
        limit: limit,
        searchBy,
        searchValue,
      })
        .then((res) => {
          setDataState((prevState) => ({
            ...prevState,
            data: res.vector_irs_reports,
            totalData: res.total,
            currentPage: res.currentPage,
            nextPage: res.nextPage,
            previousPage: res.previousPage,
          }));
        })
        .catch((err) => {
          console.error(err);
        });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [dataState.page, dataState.limit, currentUser, searchTerm]);

  useEffect(() => {
    fetchVectorControlResults();
  }, [fetchVectorControlResults]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onEditData = (item) => {
    setDataToLocalStorage("EDIT_VECTOR_CONTROL_RESULT", item);
    changeMode(PageMode.edit);
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data ini?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await IRSResultService.remove(id)
              .then((res) => {
                fetchVectorControlResults();
                toast.success("Data berhasil dihapus");
              })
              .catch((err) => {
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const monthNameFromYM = (val) => {
    if (!val) return "";

    const str = String(val);
    const parts = str.split("-");
    const mm = parts.length > 1 ? parts[1].padStart(2, "0") : null;

    if (!mm) return "";

    const opt = MonthOptions.find(
      (o) => String(o.value).padStart(2, "0") === mm
    );

    return opt ? opt.label : "";
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => onEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.id)}
            btnType={"delete"}
          />
        </div>
      ),
    },
    {
      header: "Bulan Distribusi / Intervensi",
      accessorKey: "net_distribution_month",
      cell: ({ row }) => {
        const vectorControlType = row.original.vector_controll_type;
        let dateToUse = null;

        if (vectorControlType === "IRS") {
          dateToUse = row.original.irs_date;
        } else {
          dateToUse = row.original.net_distribution_date;
        }

        if (!dateToUse) return "-";

        const month = monthNameFromYM(dateToUse);
        const year = dateToUse?.split("-")[0];

        return `${month}, ${year}`;
      },
    },
    {
      header: "Nama Desa",
      accessorKey: "urbanvillage_id",
      cell: ({ row }) => row.original.urbanvillage_id?.split("|")[1],
    },
    {
      header: "Nama Dusun",
      accessorKey: "sub_village",
      cell: ({ row }) => row.original.sub_village ?? "-",
    },
    {
      header: "Jumlah Penduduk di Dusun Tersebut",
      accessorKey: "sub_village_population",
    },
    {
      header: "Jenis Pengendalian Vektor",
      accessorKey: "vector_controll_type",
      cell: ({ row }) =>
        row.original.vector_controll_type === "Kelambu Massal Total"
          ? "Kelambu Massal"
          : row.original.vector_controll_type,
    },
    {
      header: "Tanggal IRS",
      accessorKey: "irs_date",
    },
    {
      header: "Jenis Insektisida",
      accessorKey: "insecticide_type",
    },
    {
      header: "Jumlah Insektisida Yang Digunakan",
      accessorKey: "total_insecticide",
    },
    {
      header: "Satuan Insektisida",
      accessorKey: "total_insecticide_unit",
    },
    {
      header: "Jumlah Total Sasaran Rumah",
      accessorKey: "total_targeted_house",
    },
    {
      header: "Jumlah Rumah Yang Disemprot",
      accessorKey: "total_sprayed_house",
    },
    {
      header: "Jumlah Kelambu yang diterima Desa",
      accessorKey: "total_mosquito_nets_received",
    },
    {
      header: "Jumlah Kelambu Yang Dibagikan",
      accessorKey: "total_net_distribution",
    },
    {
      header: "Jumlah Kelambu Terdistribusi Kepada Ibu Hamil",
      accessorKey: "total_nets_distributed_to_pregnant_women",
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div className="action-wrapper mt-4">
        <div className="action-button">
          {currentUser && currentUser.mode === "Online" && (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={() => changeMode(PageMode.add)}
            >
              Tambah Data
            </Button>
          )}
        </div>
        <div className="action-search">
          <InputField
            placeholder={"Cari berdasarkan nama desa..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <TableDataComponent
        columnLength={columns.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState.currentPage}
        limit={dataState.limit}
        nextPage={dataState.nextPage}
        previousPage={dataState.previousPage}
        total={dataState.totalData}
      />
    </div>
  );
};

export default TableComponent;
